import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCreateSavedSet } from "../../api/map-saved-sets/hooks";
import { showError } from "../../shared/toast";
import type { MunicipalityOption } from "./MapLayersPanel";
import "./SavedSetCreateDialog.css";

interface SavedSetCreateDialogProps {
  open: boolean;
  onClose: () => void;
  municipalityOptions: MunicipalityOption[];
  selectedMunicipalityId: string | null;
  onSuccess: (newId: string) => void;
}

const defaultName = (municipalityName: string): string =>
  `${municipalityName} ${new Date().getFullYear()}`;

export const SavedSetCreateDialog = ({
  open,
  onClose,
  municipalityOptions,
  selectedMunicipalityId,
  onSuccess
}: SavedSetCreateDialogProps): JSX.Element | null => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [municipalityId, setMunicipalityId] = useState("");
  const createMutation = useCreateSavedSet();

  useEffect(() => {
    if (open) {
      const munId = selectedMunicipalityId ?? municipalityOptions[0]?.id ?? "";
      setMunicipalityId(munId);
      const mun = municipalityOptions.find((m) => m.id === munId);
      setName(mun ? defaultName(mun.name) : "");
    }
  }, [open, selectedMunicipalityId, municipalityOptions]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      showError(
        t("map.layers.createSavedSetError", "Validation"),
        t("map.layers.nameRequired", "Name is required.")
      );
      return;
    }
    if (!municipalityId) {
      showError(
        t("map.layers.createSavedSetError", "Validation"),
        t("map.layers.municipalityRequired", "Municipality is required.")
      );
      return;
    }
    try {
      const result = await createMutation.mutateAsync({
        name: trimmed,
        municipalityPolygonId: municipalityId
      });
      onSuccess(result.id);
      onClose();
    } catch (err) {
      showError(
        t("map.layers.createSavedSetError", "Could not create saved set"),
        (err as Error)?.message ?? ""
      );
    }
  };

  if (!open) return null;

  return (
    <div
      className="saved-set-dialog-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="saved-set-create-title"
    >
      <div className="saved-set-dialog">
        <h2 id="saved-set-create-title">
          {t("map.layers.createSavedSetTitle", "Create new saved set")}
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="saved-set-dialog__field">
            <label htmlFor="saved-set-create-name">
              {t("map.layers.savedSetName", "Name")}
            </label>
            <input
              id="saved-set-create-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              autoFocus
            />
          </div>
          <div className="saved-set-dialog__field">
            <label htmlFor="saved-set-create-municipality">
              {t("map.layers.municipality", "Municipality")}
            </label>
            <select
              id="saved-set-create-municipality"
              value={municipalityId}
              onChange={(e) => setMunicipalityId(e.target.value)}
            >
              <option value="">
                {t("map.layers.selectMunicipality", "Select municipality")}
              </option>
              {municipalityOptions.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.code})
                </option>
              ))}
            </select>
          </div>
          <div className="saved-set-dialog__actions">
            <button type="button" className="map-layers-panel__btn" onClick={onClose}>
              {t("map.layers.cancel", "Cancel")}
            </button>
            <button
              type="submit"
              className="map-layers-panel__btn map-layers-panel__btn--primary"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending
                ? t("map.layers.saving", "Saving...")
                : t("map.layers.save", "Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
